import React, { useCallback } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { System } from '@workwave-tidal/core/icons/illustrations'
import Typography from '@mui/material/Typography'
import { Add } from '@workwave-tidal/core/icons/Add'
import Button from '@mui/material/Button'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import {
  defaultColumns,
  defaultColVis,
} from '@user/components/Settings/SCWebhooks/hooks/tableColumn'
import { WWMXGrid } from '@user/components/xGrid'
import { useGetColumnData } from '@user/components/Settings/SCWebhooks/hooks/useGetColumnData'
import { useActions } from '@user/components/Settings/SCWebhooks/hooks/useActions'
import { useData } from '@user/components/Settings/SCWebhooks/hooks/useData'
import { SCWebhookSideSheet } from '@user/components/Settings/SCWebhooks/SCWebhookSideSheet/SCWebhookSideSheet'
import {
  IUserPreferenceGeneralListItemType,
  saveGeneralPreferences,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'
import { GridDensity, GridState } from '@mui/x-data-grid-pro'
import { useAppDispatch } from '@user/store/hooks'
import { useSelector } from 'react-redux'
import { OpenInNew } from '@workwave-tidal/core/icons'
import { ServiceConfig } from '@user/store/services/config'
import { ConfirmationDialog } from '@sales-center/components'

const SCWebhooks = () => {
  const tidalThemeActive = useTidalThemeActive()
  const dispatch = useAppDispatch()
  const userGeneralPreferences = useSelector(userGeneralPreferencesList)

  const {
    allSCWebHooks,
    webhookLoading,
    orderBy,
    page,
    perPage,
    deleteWebhook,
    sideSheetOpen,
    handleSideSheetClose,
    handleCreateEditWebhooks,
    onChangeWebHookStatus,
    cancelDialogOpen,
    hideCancelDialog,
  } = useData()

  const {
    loading: colsLoading,
    columns,
    columnVisibility,
    apiRef,
  } = useGetColumnData({
    handleEditClick: handleCreateEditWebhooks,
    handleDeleteRequest: deleteWebhook,
    onChangeStatus: onChangeWebHookStatus,
  })

  const {
    onColumnWidthChange,
    onSaveColumnState,
    onSortModelChange,
    sortModel,
    handlePaginationModelChange,
  } = useActions({ columns, orderBy })

  const densityModeUserGeneralPreference = userGeneralPreferences?.find(
    (row) =>
      row?.type == IUserPreferenceGeneralListItemType.SCWebHooksDensityMode,
  )

  const stringValue = densityModeUserGeneralPreference?.value ?? 'standard'
  const densityMode = stringValue as GridDensity

  const onDensityChange = (density: string) => {
    if (densityMode != density) {
      dispatch(
        saveGeneralPreferences({
          type: IUserPreferenceGeneralListItemType.SCWebHooksDensityMode,
          value: density,
        }),
      )
    }
  }

  const onStateChange = (state: GridState) => {
    if (state.density) {
      onDensityChange(state.density.value)
    }
  }

  const openApiDocumentation = useCallback(() => {
    window.open(`${ServiceConfig.baseAPIUrl}/swagger/index.html`, '_blank')
  }, [])

  return (
    <Box minHeight={'calc(100svh - 78px)'} bgcolor={'#F9FAFB'}>
      <Box px={2} pt={2}>
        <Stack
          width="100%"
          direction={{ sm: 'column', md: 'row' }}
          justifyContent={{ sm: 'flex-start', md: 'space-between' }}
          bgcolor={'#E7F5F6'}
          p={1.5}
          borderRadius="4px"
          gap="15px"
          mb={1.5}
          sx={{
            border: '1px solid #E0E0E0',
          }}
        >
          <Stack display={'flex'} direction="row" alignItems="center">
            <System size={60} />
            <Stack ml="18px">
              <Typography variant="h6">API Settings</Typography>
              <Typography
                sx={{ color: (theme) => theme.palette.text.primary }}
                variant="body2"
              >
                Manage API access and webhooks
              </Typography>
            </Stack>
          </Stack>

          <Stack
            display={'flex'}
            justifyContent={{ sm: 'flex-start', md: 'flex-end' }}
            direction="row"
            alignItems="center"
            gap={2}
            minWidth={tidalThemeActive ? '407px' : '395px'}
          >
            <Button
              sx={{ minWidth: 'fit-content', mr: '16px' }}
              color="primary"
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleCreateEditWebhooks()}
            >
              Create Webhook
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Stack
        flexDirection="row"
        justifyContent="space-between"
        mt="14px"
        px={2}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          paddingX="7px"
          gap={3}
        >
          <Typography variant="subtitle4">Webhooks:</Typography>
          <Typography variant="body2">
            Available &nbsp;&nbsp;<b>{10 - allSCWebHooks.length}</b>
          </Typography>
          <Typography variant="body2">
            Used &nbsp;&nbsp;<b>{allSCWebHooks.length}</b>
          </Typography>
        </Stack>
        <Button
          variant="text"
          endIcon={<OpenInNew />}
          sx={{ color: '#1565C0' }}
          onClick={openApiDocumentation}
        >
          API Documentation
        </Button>
      </Stack>

      <WWMXGrid
        virtualScrollerHeight={
          densityMode === 'compact'
            ? 375
            : densityMode === 'standard'
              ? 393
              : 409
        }
        applyCustomStyles={true}
        apiRef={apiRef}
        loading={colsLoading || webhookLoading}
        // columns
        columnVisibilityModel={columnVisibility}
        columns={colsLoading ? [] : columns}
        onColumnWidthChange={(e) =>
          onColumnWidthChange(e?.colDef, columns, columnVisibility)
        }
        columnsSelectorProps={{
          saveCallback: (e) => onSaveColumnState(e, columns),
          defaultColumns: defaultColumns,
          defaultVisibility: defaultColVis,
          lockedColumns: ['actions', 'status', 'endpointUrl'],
        }}
        pinnedColumns={{ left: ['actions', 'status'] }}
        //state
        isDensityEnable
        density={densityMode}
        onStateChange={onStateChange}
        // rows
        rows={colsLoading || webhookLoading ? [] : allSCWebHooks}
        rowCount={allSCWebHooks.length}
        // pagination
        paginationModel={{ page: page - 1, pageSize: perPage }}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[10, 25, 50]}
        // sorting
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        width="100%"
        hideDefaultBtn={true}
      />

      {sideSheetOpen && (
        <SCWebhookSideSheet
          handleClose={handleSideSheetClose}
          open={sideSheetOpen}
        />
      )}

      <ConfirmationDialog
        cancelClick={hideCancelDialog}
        message={
          'You have already created the maximum number of webhooks. \nYou must delete one before you can create another.'
        }
        cancelButtonText={'Close'}
        open={cancelDialogOpen}
        title="No Webhooks Available"
      />
    </Box>
  )
}

export default SCWebhooks
